/**
 * EquipmentAlertsThresholdsTabs — abriga 2 abas relacionadas ao equipment:
 *
 *   1. Alertas    (default) — eventos ativos + histórico daquele equipment
 *   2. Thresholds           — config de regras/limites
 *
 * Ambos existiam antes empilhados na mesma área do EquipmentDetail. Separar
 * em abas reduz ruído visual: alertas é "leitura reativa" (o que aconteceu?)
 * e thresholds é "configuração" (o que quero monitorar?).
 *
 * Aba ativa fica no query string (?tab=alerts|thresholds) pra sobreviver a
 * refresh e permitir link direto.
 */

import { useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { AlertTriangle, SlidersHorizontal } from "lucide-react";

import { EquipmentAlertsCard } from "./EquipmentAlertsCard";
import { EquipmentThresholdsCard } from "./EquipmentThresholdsCard";

type Tab = "alerts" | "thresholds";
const DEFAULT_TAB: Tab = "alerts";

interface Props {
  equipmentId: string;
  tenantId: string;
}

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
  {
    key: "alerts",
    label: "Alertas",
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  {
    key: "thresholds",
    // Rótulo em português; a chave da URL continua `thresholds` para não
    // quebrar link salvo. Nome de tela e identificador não precisam ser a
    // mesma palavra — e aqui a palavra que o cliente lê importa mais.
    label: "Limites",
    icon: <SlidersHorizontal className="h-4 w-4" />,
  },
];

export function EquipmentAlertsThresholdsTabs({ equipmentId, tenantId }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const activeTab: Tab =
    rawTab === "thresholds" || rawTab === "alerts" ? rawTab : DEFAULT_TAB;

  function setTab(tab: Tab) {
    const next = new URLSearchParams(searchParams);
    if (tab === DEFAULT_TAB) next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="mt-6">
      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-surface-200 dark:border-slate-800">
        {TABS.map((t) => {
          const isActive = t.key === activeTab;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={clsx(
                "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium",
                "border-b-2 -mb-px transition-colors",
                isActive
                  ? "border-brand-600 text-brand-700 dark:text-brand-300"
                  : "border-transparent text-slate-500 dark:text-slate-400 " +
                    "hover:text-slate-700 dark:hover:text-slate-200",
              )}
              aria-current={isActive ? "page" : undefined}
            >
              {t.icon}
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Panel */}
      <div className="mt-4">
        {activeTab === "alerts" && (
          <EquipmentAlertsCard equipmentId={equipmentId} tenantId={tenantId} />
        )}
        {activeTab === "thresholds" && (
          <EquipmentThresholdsCard equipmentId={equipmentId} tenantId={tenantId} />
        )}
      </div>
    </div>
  );
}
